import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdBuild, MdRefresh } from 'react-icons/md';
import { getRentalStatus } from '../../../api/services/rentalService';
import { getMyMaintenanceRequests, submitMaintenanceRequest } from '../../../api/services/maintenanceService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './Maintenance.css';

const STATUS_FILTERS = ['OPEN', 'HANDLED', 'COMPLETED', 'CANCELLED'];

const formatDateTime = (isoValue) => {
  if (!isoValue) return '—';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return String(isoValue);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusLabel = (value) => {
  const status = String(value || '').toUpperCase();
  if (status === 'OPEN') return 'Open';
  if (status === 'HANDLED') return 'Handled';
  if (status === 'COMPLETED') return 'Completed';
  if (status === 'CANCELLED') return 'Cancelled';
  return status || 'Unknown';
};

const Maintenance = () => {
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [description, setDescription] = useState('');
  const [activeFilter, setActiveFilter] = useState('OPEN');
  const [requests, setRequests] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [rentalRes, requestsRes] = await Promise.all([
        getRentalStatus().catch(() => null),
        getMyMaintenanceRequests(),
      ]);

      const rentalData = rentalRes?.status === 'success' && rentalRes?.data ? rentalRes.data : null;
      const requestsData = Array.isArray(requestsRes?.data) ? requestsRes.data : [];
      requestsData.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

      setRental(rentalData);
      setRequests(requestsData);
    } catch (e) {
      setError(e.message || 'Could not load maintenance requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const propertyId = rental?.propertyId || rental?.property?.id || null;
  const unitId = rental?.unitId || rental?.unit?.id || null;
  const hasActiveRental = Boolean(propertyId && unitId);

  const requestCounts = useMemo(() => {
    const counts = { OPEN: 0, HANDLED: 0, COMPLETED: 0, CANCELLED: 0 };
    requests.forEach((request) => {
      const key = String(request.status || '').toUpperCase();
      if (counts[key] != null) counts[key] += 1;
    });
    return counts;
  }, [requests]);

  const filteredRequests = useMemo(
    () => requests.filter((request) => String(request.status || '').toUpperCase() === activeFilter),
    [requests, activeFilter]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const cleanDescription = description.trim();
    if (!cleanDescription) {
      setSubmitError('Please describe the maintenance issue.');
      setSubmitSuccess('');
      return;
    }
    if (!hasActiveRental) {
      setSubmitError('You need an active unit assignment to submit a maintenance request.');
      setSubmitSuccess('');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');
      setSubmitSuccess('');
      await submitMaintenanceRequest(propertyId, unitId, { description: cleanDescription });
      setDescription('');
      setSubmitSuccess('Maintenance request submitted.');
      await loadData();
      setActiveFilter('OPEN');
    } catch (e) {
      setSubmitError(e.message || 'Failed to submit maintenance request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tenant-maintenance-page">
      <CollapsibleTopPanel id="tenant-maintenance-top-panel" defaultOpenOnMobile={false}>
        <header className="tenant-maintenance-header">
          <div>
            <h1 className="tenant-maintenance-title">Maintenance</h1>
            <p className="tenant-maintenance-subtitle">
              Submit issues for your current unit and track request progress.
            </p>
          </div>
          <button
            type="button"
            className="tenant-maintenance-refresh"
            onClick={loadData}
            disabled={loading}
            aria-label="Refresh maintenance requests"
          >
            <MdRefresh className={loading ? 'spin' : ''} />
          </button>
        </header>
      </CollapsibleTopPanel>

      <section className="tenant-maintenance-submit-card">
        <div className="tenant-maintenance-submit-header">
          <MdBuild />
          <h2>Submit a Request</h2>
        </div>
        <p className="tenant-maintenance-unit-label">
          {hasActiveRental
            ? `${rental?.propertyName || 'Property'} • Unit ${rental?.unitNumber || rental?.unit?.unitNumber || '—'}`
            : 'No active unit assignment found'}
        </p>
        <form onSubmit={handleSubmit} className="tenant-maintenance-form">
          <label htmlFor="maintenance-description">Issue description</label>
          <textarea
            id="maintenance-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Example: Toilet not flushing"
            rows={4}
            maxLength={600}
            disabled={submitting || !hasActiveRental}
          />
          {submitError ? <p className="tenant-maintenance-feedback error">{submitError}</p> : null}
          {submitSuccess ? <p className="tenant-maintenance-feedback success">{submitSuccess}</p> : null}
          <button type="submit" disabled={submitting || !hasActiveRental}>
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </section>

      <section className="tenant-maintenance-list-section">
        <div className="tenant-maintenance-filters">
          {STATUS_FILTERS.map((status) => (
            <button
              key={status}
              type="button"
              className={`tenant-maintenance-filter-btn${activeFilter === status ? ' active' : ''}`}
              onClick={() => setActiveFilter(status)}
            >
              {statusLabel(status)} ({requestCounts[status] || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="tenant-maintenance-state">Loading maintenance requests...</div>
        ) : error ? (
          <div className="tenant-maintenance-state error">{error}</div>
        ) : filteredRequests.length === 0 ? (
          <div className="tenant-maintenance-state">No {statusLabel(activeFilter).toLowerCase()} requests yet.</div>
        ) : (
          <div className="tenant-maintenance-list">
            {filteredRequests.map((request) => {
              const normalizedStatus = String(request.status || '').toUpperCase();
              return (
                <article key={request.id} className="tenant-maintenance-item">
                  <div className="tenant-maintenance-item-top">
                    <h3>{request.description || 'Maintenance request'}</h3>
                    <span className={`tenant-maintenance-status tenant-maintenance-status--${normalizedStatus.toLowerCase()}`}>
                      {statusLabel(normalizedStatus)}
                    </span>
                  </div>
                  <p className="tenant-maintenance-item-meta">
                    {request.propertyName || 'Property'} • Unit {request.unitNumber || '—'}
                  </p>
                  <p className="tenant-maintenance-item-meta">
                    Submitted: {formatDateTime(request.createdAt)}
                  </p>
                  {request.landlordNotes ? (
                    <p className="tenant-maintenance-notes">Landlord notes: {request.landlordNotes}</p>
                  ) : null}
                  {request.resolvedAt ? (
                    <p className="tenant-maintenance-item-meta">Resolved: {formatDateTime(request.resolvedAt)}</p>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Maintenance;
